import React from 'react';

export const UPLOAD_MAX_BYTES = 15 * 1024 * 1024;
export const UPLOAD_MAX_DIM = 4096;
export const UPLOAD_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

export const uploadStatusLabel = (status, progress) => ({
    idle: '',
    validating: 'Validating image…',
    ready: 'Ready to upload',
    uploading: progress >= 70 ? 'Checking and converting…' : 'Uploading…',
    done: 'Image uploaded!',
    rejected: 'Image rejected',
    error: 'Upload failed',
}[status] || '');

const CloudUploadPanel = ({
    status,
    preview,
    file,
    dimensions,
    progress,
    error,
    doneLabel,
    onFileChosen,
    onReset,
    onSubmit,
    compact = false,
}) => {
    const fileInputRef = React.useRef(null);

    const willBeResized = dimensions && Math.max(dimensions.w, dimensions.h) > UPLOAD_MAX_DIM;

    const statusLabel = status === 'done' && doneLabel
        ? doneLabel
        : uploadStatusLabel(status, progress);

    const progressBarColor = status === 'rejected' || status === 'error'
        ? 'bg-red-500'
        : status === 'done'
            ? 'bg-green-500'
            : 'bg-blue-500';

    const handleFileChange = (e) => {
        const chosen = e.target.files?.[0];
        if (chosen) onFileChosen(chosen);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) onFileChosen(dropped);
    };

    const handleReset = () => {
        if (fileInputRef.current) fileInputRef.current.value = '';
        onReset();
    };

    const c = {
        wrap: compact ? 'p-3 gap-2' : 'p-5 gap-4',
        info: compact ? 'text-[11px] leading-snug' : 'text-sm',
        warn: compact ? 'text-[11px] leading-snug' : 'text-sm',
        dropPad: compact ? 'p-4 gap-1.5' : 'p-8 gap-3',
        dropLead: compact ? 'text-sm' : 'text-base',
        icon: compact ? 22 : 32,
        thumb: compact ? 'h-24' : 'h-40',
        status: compact ? 'text-xs' : 'text-sm',
        btn: compact ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-sm',
        btnPrimary: compact ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm',
    };

    return (
        <div className={`flex flex-col flex-1 overflow-y-auto ${c.wrap}`}>
            <p className={`text-coal/50 ${c.info}`}>
                Max size: <strong>{Math.round(UPLOAD_MAX_BYTES / (1024 * 1024))}MB</strong> · Images are kept at their original resolution and converted to <strong>WebP</strong>.
                Only images longer than <strong>{UPLOAD_MAX_DIM}px</strong> on their longest edge are scaled down.
            </p>
            <p className={`text-red-400 ${c.warn}`}>
                All images are checked with an AI moderation service on upload, this tries to ensure content is "Safe for Work". Any images which violate our{' '}
                <a href="/community-guidelines" target="_blank" rel="noreferrer" className="underline hover:text-red-300">Community Guidelines</a>
                {' '}are subject to removal and, depending on the subject of the image, further action may be taken against you.
            </p>

            {status === 'idle' || status === 'validating' ? (
                <div
                    className={`border-2 border-dashed border-black/25 hover:border-black/50 bg-black/[0.02] hover:bg-black/[0.04] transition-colors flex flex-col items-center justify-center cursor-pointer ${c.dropPad}`}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <svg viewBox="0 0 24 24" width={c.icon} height={c.icon} className="text-coal/30"><path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" /></svg>
                    <p className={`text-coal/50 text-center ${c.dropLead}`}>Click to choose an image, or drag and drop here</p>
                    <p className="text-xs text-coal/30">JPEG · PNG · GIF · WebP</p>
                    <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" className="hidden" onChange={handleFileChange} />
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {preview && (
                        <div className={`border border-black/20 bg-black/5 flex items-center justify-center overflow-hidden ${c.thumb}`}>
                            <img src={preview} alt="Preview" className="max-h-full max-w-full object-contain" />
                        </div>
                    )}

                    {file && dimensions && (
                        <p className="text-xs text-coal/50">
                            {file.name} · {dimensions.w}×{dimensions.h}px · {(file.size / 1024).toFixed(0)}KB
                            {willBeResized && (
                                <span className="text-coal/40"> · will be scaled to fit {UPLOAD_MAX_DIM}px</span>
                            )}
                        </p>
                    )}

                    <p className={`font-bold ${c.status} ${status === 'rejected' || status === 'error' ? 'text-red-600' : status === 'done' ? 'text-green-600' : 'text-coal/70'}`}>
                        {statusLabel}
                    </p>

                    {(status === 'uploading' || status === 'done') && (
                        <div className="h-2 w-full bg-black/10 overflow-hidden">
                            <div
                                className={`h-full transition-all duration-300 ${progressBarColor}`}
                                style={{ width: `${progress}%` }}
                            />
                        </div>
                    )}

                    {error && (
                        <p className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{error}</p>
                    )}
                </div>
            )}

            <div className="flex items-center gap-3">
                {(status === 'ready' || status === 'rejected' || status === 'error') && (
                    <>
                        <button
                            type="button"
                            onClick={handleReset}
                            className={`border border-black/20 font-bold text-coal/60 hover:text-coal hover:bg-black/5 transition-colors ${c.btn}`}
                        >
                            Choose different image
                        </button>
                        {status === 'ready' && (
                            <button
                                type="button"
                                onClick={onSubmit}
                                className={`bg-coal text-offwhite font-bold uppercase tracking-wide hover:bg-coal/80 transition-colors ${c.btnPrimary}`}
                            >
                                Upload
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CloudUploadPanel;
